import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Reparații PC & Laptop",
  "Rețele & Internet",
  "Camere de supraveghere",
  "Web design & găzduire",
  "Suport IT pentru firme",
  "Cloud & Backup",
  "Altceva",
];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Te rog completează toate câmpurile obligatorii.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Cerere trimisă! Te contactăm în cel mai scurt timp.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "",
        city: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Nu am putut trimite cererea. Încearcă din nou.";
      toast.error(typeof detail === "string" ? detail : "Eroare la trimitere.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-16 lg:py-24 bg-sandwarm"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="overline text-clay">DATE DE CONTACT</span>
          <p className="mt-6 text-inksoft max-w-md">
            Alege canalul care ți se potrivește. Toate răspund în același
            interval.
          </p>

          <div className="mt-10 space-y-5">
            <ContactRow
              icon={Phone}
              label="Telefon"
              value="0799 301 302"
              href="tel:+40799301302"
              testId="contact-phone"
            />
            <ContactRow
              icon={Mail}
              label="Email"
              value="contact@itview.ro"
              href="mailto:contact@itview.ro"
              testId="contact-email"
            />
            <ContactRow
              icon={MapPin}
              label="Acoperire"
              value="On-site și remote"
              testId="contact-locations"
            />
            <ContactRow
              icon={Clock}
              label="Program"
              value="Luni–Vineri · 09:00 – 19:00 · Sâmbăta on-call"
              testId="contact-hours"
            />
          </div>

          <div className="mt-12 p-6 rounded-2xl border border-line bg-white">
            <p className="font-display text-lg font-semibold">
              Urgență după ore?
            </p>
            <p className="text-sm text-inksoft mt-1">
              Clienții cu abonament Business Care au linie dedicată 24/7 și
              răspuns garantat sub 1 oră.
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 rounded-2xl border border-line bg-white p-8 lg:p-10"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Nume *" testId="field-name">
              <Input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Numele tău"
                required
              />
            </Field>
            <Field label="Telefon *" testId="field-phone">
              <Input
                data-testid="contact-input-phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="07xx xxx xxx"
                required
              />
            </Field>
            <Field label="Email *" testId="field-email">
              <Input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="email@domeniu.ro"
                required
              />
            </Field>
            <Field label="Oraș" testId="field-city">
              <Input
                data-testid="contact-input-city"
                value={form.city}
                onChange={update("city")}
                placeholder="Orașul tău"
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Serviciu dorit *" testId="field-service">
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((s) => ({ ...s, service: v }))}
                >
                  <SelectTrigger
                    data-testid="contact-select-service"
                    className="bg-white border-line h-10"
                  >
                    <SelectValue placeholder="Alege un serviciu" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Detalii cerere *" testId="field-message">
                <Textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Descrie pe scurt problema sau proiectul tău…"
                  rows={5}
                  required
                />
              </Field>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <p className="text-xs text-inksoft max-w-md">
              Prin trimiterea formularului confirmi că am voie să te contactez
              în legătură cu cererea ta. Datele nu sunt partajate cu terți.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ink text-sand font-semibold text-sm hover:bg-clay hover:text-white transition-colors disabled:opacity-60"
            >
              {loading ? "Se trimite…" : "Trimite cererea"}
              <Send size={14} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href, testId }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      data-testid={testId}
      className="flex items-start gap-4 group"
    >
      <span className="w-10 h-10 rounded-lg bg-white border border-line grid place-items-center group-hover:bg-clay group-hover:border-clay transition-colors">
        <Icon
          size={16}
          className="text-ink group-hover:text-white transition-colors"
        />
      </span>
      <div>
        <div className="overline text-inksoft">{label}</div>
        <div className="mt-1 font-semibold text-ink">{value}</div>
      </div>
    </Wrapper>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col gap-2">
      <Label className="overline text-inksoft">{label}</Label>
      {children}
    </div>
  );
}
